import React, { useState, useEffect } from 'react';
import {
  Sparkles, Instagram, Facebook, Loader2, Copy, Wand2, Save,
  CheckCircle2, FileText, History, Trash2, RotateCcw, Send,
  Image as ImageIcon, Heart, Megaphone, Briefcase, FileDown, BookOpen, Home, Layout,
  Package, Download, Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import axiosInstance from '../../api/axios';
import SendByEmailDialog from '../../components/SendByEmailDialog';
import { toast } from 'sonner';

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E1306C' },
  { id: 'facebook', label: 'Facebook',  icon: Facebook, color: '#1877F2' },
];

const TONES = [
  { id: 'warm',         label: 'Warm',        icon: Heart },
  { id: 'elegant',      label: 'Elegant',     icon: Sparkles },
  { id: 'playful',      label: 'Verspielt',   icon: Wand2 },
  { id: 'professional', label: 'Professionell', icon: Briefcase },
  { id: 'inspiring',    label: 'Inspirierend', icon: Megaphone },
];

const ContentStudio = () => {
  // Mode: 'social' | 'blog' | 'homepage'
  const [mode, setMode] = useState('social');

  // ---- Social form state ----
  const [platform, setPlatform] = useState('instagram');
  const [tone, setTone] = useState('warm');
  const [prompt, setPrompt] = useState('');
  const [audience, setAudience] = useState('');
  const [ctaLink, setCtaLink] = useState('');
  const [includeProducts, setIncludeProducts] = useState(true);

  // ---- Blog form state ----
  const [blogTopic, setBlogTopic] = useState('');
  const [blogLength, setBlogLength] = useState('medium');
  const [blogKeywords, setBlogKeywords] = useState('');
  const [blogAudience, setBlogAudience] = useState('');

  // ---- Homepage section state ----
  const [hpSectionType, setHpSectionType] = useState('hero');
  const [hpTopic, setHpTopic] = useState('');

  // ---- Catalog PDF state (Phase 3) ----
  const [catCategories, setCatCategories] = useState([]);
  const [catSubsByCat, setCatSubsByCat] = useState({});
  const [catCategory, setCatCategory] = useState('');
  const [catSubcategory, setCatSubcategory] = useState('');
  const [catMinPrice, setCatMinPrice] = useState('');
  const [catMaxPrice, setCatMaxPrice] = useState('');
  const [catHandmadeOnly, setCatHandmadeOnly] = useState(false);
  const [catIncludeImages, setCatIncludeImages] = useState(true);
  const [catTitle, setCatTitle] = useState('');
  const [catIntro, setCatIntro] = useState('');
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineInstruction, setRefineInstruction] = useState('');
  const [draft, setDraft] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [drafts, setDrafts] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Email dialog (used for both Catalog PDF and Draft PDF)
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailFiles, setEmailFiles] = useState([]);
  const [emailDefaults, setEmailDefaults] = useState({ subject: '', message: '' });

  const loadDrafts = async () => {
    try {
      const res = await axiosInstance.get('/content-studio/drafts');
      setDrafts(res.data.drafts || []);
    } catch (_) { /* silent */ }
  };

  useEffect(() => { loadDrafts(); }, []);

  // Load categories the first time the catalog tab is opened
  useEffect(() => {
    if (mode === 'catalog' && catCategories.length === 0) {
      (async () => {
        try {
          const res = await axiosInstance.get('/catalog-pdf/categories');
          setCatCategories(res.data.categories || []);
          setCatSubsByCat(res.data.subcategories || {});
        } catch (_) {
          toast.error('Kategorien konnten nicht geladen werden');
        }
      })();
    }
  }, [mode, catCategories.length]);

  const handleDownloadCatalog = async () => {
    setCatalogLoading(true);
    try {
      const params = new URLSearchParams();
      if (catCategory) params.append('category', catCategory);
      if (catSubcategory) params.append('subcategory', catSubcategory);
      if (catMinPrice) params.append('min_price', catMinPrice);
      if (catMaxPrice) params.append('max_price', catMaxPrice);
      if (catHandmadeOnly) params.append('handmade_only', 'true');
      params.append('include_images', catIncludeImages ? 'true' : 'false');
      if (catTitle.trim()) params.append('title', catTitle.trim());
      if (catIntro.trim()) params.append('intro', catIntro.trim());

      const res = await axiosInstance.get(
        `/catalog-pdf/generate?${params.toString()}`,
        { responseType: 'blob', timeout: 60000 }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const slug = (catCategory || 'alle').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 40);
      link.setAttribute('download', `homeyoursoul-katalog-${slug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Katalog-PDF heruntergeladen');
    } catch (err) {
      const detail = err.response?.data?.detail ||
        (err.response?.status === 404 ? 'Keine Produkte mit diesen Filtern gefunden' : 'Katalog-Erstellung fehlgeschlagen');
      toast.error(detail);
    } finally {
      setCatalogLoading(false);
    }
  };

  // Build a lazy getter for the catalog PDF blob with the current filters
  const openEmailForCatalog = () => {
    const slug = (catCategory || 'alle').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 40);
    const filename = `homeyoursoul-katalog-${slug}.pdf`;
    setEmailFiles([{
      filename,
      getBlob: async () => {
        const params = new URLSearchParams();
        if (catCategory) params.append('category', catCategory);
        if (catSubcategory) params.append('subcategory', catSubcategory);
        if (catMinPrice) params.append('min_price', catMinPrice);
        if (catMaxPrice) params.append('max_price', catMaxPrice);
        if (catHandmadeOnly) params.append('handmade_only', 'true');
        params.append('include_images', catIncludeImages ? 'true' : 'false');
        if (catTitle.trim()) params.append('title', catTitle.trim());
        if (catIntro.trim()) params.append('intro', catIntro.trim());
        const res = await axiosInstance.get(
          `/catalog-pdf/generate?${params.toString()}`,
          { responseType: 'blob', timeout: 60000 }
        );
        return new Blob([res.data], { type: 'application/pdf' });
      },
    }]);
    setEmailDefaults({
      subject: catTitle.trim() || `Produktkatalog Homeyoursoul${catCategory ? ' – ' + catCategory : ''}`,
      message: 'Guten Tag,\n\nanbei wie gewünscht unser aktueller Produktkatalog.\n\nHerzliche Grüße\nYasemin Kurt-Yilmaz\nHomeyoursoul',
    });
    setEmailOpen(true);
  };

  const openEmailForDraft = () => {
    if (!draft) return;
    const safeTitle = (draft.title || 'entwurf').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60);
    const filename = `homeyoursoul-${draft.content_type || 'social'}-${safeTitle}.pdf`;
    setEmailFiles([{
      filename,
      getBlob: async () => {
        const res = await axiosInstance.get(
          `/content-studio/drafts/${draft.id}/export-pdf`,
          { responseType: 'blob', timeout: 60000 }
        );
        return new Blob([res.data], { type: 'application/pdf' });
      },
    }]);
    setEmailDefaults({
      subject: draft.title || 'Inhalt von Homeyoursoul',
      message: 'Guten Tag,\n\nanbei der gewünschte Inhalt.\n\nHerzliche Grüße\nYasemin Kurt-Yilmaz\nHomeyoursoul',
    });
    setEmailOpen(true);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      let res;
      if (mode === 'social') {
        if (prompt.trim().length < 4) {
          toast.error('Bitte beschreibe was du posten möchtest.');
          setGenerating(false);
          return;
        }
        res = await axiosInstance.post('/content-studio/generate', {
          platform, tone, prompt,
          target_audience: audience || null,
          cta_link: ctaLink || null,
          include_products: includeProducts,
        });
      } else if (mode === 'blog') {
        if (blogTopic.trim().length < 4) {
          toast.error('Bitte gib ein Thema für den Blog-Beitrag ein.');
          setGenerating(false);
          return;
        }
        res = await axiosInstance.post('/content-studio/blog/generate', {
          topic: blogTopic,
          tone,
          length: blogLength,
          target_audience: blogAudience || null,
          keywords: blogKeywords ? blogKeywords.split(',').map(k => k.trim()).filter(Boolean) : null,
          include_products: includeProducts,
        });
      } else {
        if (hpTopic.trim().length < 4) {
          toast.error('Bitte beschreibe den Inhalt der Sektion.');
          setGenerating(false);
          return;
        }
        res = await axiosInstance.post('/content-studio/homepage-section/generate', {
          section_type: hpSectionType,
          topic: hpTopic,
          tone,
        });
      }
      setDraft(res.data);
      setRefineInstruction('');
      loadDrafts();
      toast.success('Entwurf erstellt!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generierung fehlgeschlagen');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!draft) return;
    setDownloading(true);
    try {
      const res = await axiosInstance.get(
        `/content-studio/drafts/${draft.id}/export-pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = (draft.title || 'entwurf').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60);
      link.setAttribute('download', `homeyoursoul-${draft.content_type || 'social'}-${safeTitle}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (err) {
      toast.error('PDF-Download fehlgeschlagen');
    } finally {
      setDownloading(false);
    }
  };

  const handleRefine = async () => {
    if (!draft || refineInstruction.trim().length < 2) {
      toast.error('Bitte beschreibe deine Anpassung.');
      return;
    }
    setRefining(true);
    try {
      const res = await axiosInstance.post('/content-studio/refine', {
        draft_id: draft.id,
        instruction: refineInstruction,
      });
      setDraft(res.data);
      setRefineInstruction('');
      loadDrafts();
      toast.success('Post angepasst!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anpassung fehlgeschlagen');
    } finally {
      setRefining(false);
    }
  };

  const handleSaveEdits = async () => {
    if (!draft) return;
    try {
      // Use generic /full endpoint that accepts blog/homepage-specific fields
      const payload = { ...draft };
      delete payload.id;
      delete payload._id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.history;
      const res = await axiosInstance.put(`/content-studio/drafts/${draft.id}/full`, payload);
      setDraft(res.data);
      loadDrafts();
      toast.success('Änderungen gespeichert');
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const handleRequestApproval = async () => {
    if (!draft) return;
    try {
      await axiosInstance.post(`/content-studio/drafts/${draft.id}/request-approval`);
      toast.success('Freigabe-Anfrage ins Postfach gelegt');
      setDraft({ ...draft, status: 'awaiting_approval' });
      loadDrafts();
    } catch (err) {
      toast.error('Anfrage fehlgeschlagen');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In die Zwischenablage kopiert');
  };

  const handleLoadDraft = async (id) => {
    try {
      const res = await axiosInstance.get(`/content-studio/drafts/${id}`);
      setDraft(res.data);
      setShowHistory(false);
    } catch (_) { toast.error('Laden fehlgeschlagen'); }
  };

  const handleDeleteDraft = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Entwurf wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/content-studio/drafts/${id}`);
      loadDrafts();
      if (draft?.id === id) setDraft(null);
      toast.success('Entwurf gelöscht');
    } catch (_) { toast.error('Löschen fehlgeschlagen'); }
  };

  const platformMeta = PLATFORMS.find((p) => p.id === platform);

  return (
    <div className="min-h-screen p-3 sm:p-6" style={{ backgroundColor: '#F5EDE4' }} data-testid="content-studio-page">
      <div className="max-w-5xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#E8DCC8]">
              <Sparkles className="h-6 w-6 text-[#5D4E3C]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#5D4E3C]">Content-Studio</h1>
              <p className="text-xs sm:text-sm text-[#8B7355]">KI-Generator für Instagram & Facebook Posts</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowHistory(!showHistory)}
            className="rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#E8DCC8]"
            data-testid="cs-toggle-history-btn"
          >
            <History className="h-4 w-4 mr-2" /> Entwürfe ({drafts.length})
          </Button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 p-1 bg-white border border-[#D4C4B0]" data-testid="cs-mode-tabs">
          {[
            { id: 'social', label: 'Social Post', icon: Instagram },
            { id: 'blog', label: 'Blog-Beitrag', icon: BookOpen },
            { id: 'homepage', label: 'Homepage-Sektion', icon: Layout },
            { id: 'catalog', label: 'Katalog-PDF', icon: Package },
          ].map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => { setMode(m.id); setDraft(null); }}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-[#5D4E3C] text-white'
                    : 'bg-transparent text-[#5D4E3C] hover:bg-[#F5F0E8]'
                }`}
                data-testid={`cs-mode-${m.id}`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{m.label}</span>
              </button>
            );
          })}
        </div>

        {/* History panel */}
        {showHistory && (
          <Card className="rounded-none border-[#D4C4B0]" data-testid="cs-history-panel">
            <CardContent className="p-0">
              {drafts.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#8B7355]">Noch keine Entwürfe gespeichert</div>
              ) : (
                <ul className="divide-y divide-[#E8DCC8]">
                  {drafts.map((d) => {
                    const PlatformIcon = d.platform === 'facebook' ? Facebook : Instagram;
                    return (
                      <li
                        key={d.id}
                        onClick={() => handleLoadDraft(d.id)}
                        className="flex items-start gap-3 p-3 hover:bg-[#FAF5EE] cursor-pointer"
                        data-testid={`cs-history-item-${d.id}`}
                      >
                        <PlatformIcon className="h-4 w-4 mt-1 text-[#8B7355] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#5D4E3C] truncate">{d.title || 'Ohne Titel'}</p>
                          <p className="text-xs text-[#8B7355] truncate">{d.caption?.slice(0, 90)}…</p>
                          <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 bg-[#E8DCC8] text-[#5D4E3C]">{d.status}</span>
                        </div>
                        <button
                          onClick={(e) => handleDeleteDraft(d.id, e)}
                          className="text-red-500 hover:text-red-700"
                          aria-label="Entwurf löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {mode === 'catalog' && (
          <Card className="rounded-none border-[#D4C4B0]" data-testid="cs-catalog-card">
            <CardHeader className="border-b border-[#E8DCC8]">
              <CardTitle className="text-[#5D4E3C] flex items-center gap-2">
                <Package className="h-5 w-5" />
                Katalog-PDF aus Sortiment erstellen
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <p className="text-sm text-[#8B7355]">
                Erstelle ein elegantes PDF deiner Produkte – gefiltert nach Kategorie, Preis und mehr.
                Ideal für Lookbooks, Wiederverkäufer-Anfragen oder Messen.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#5D4E3C] text-xs">Kategorie</Label>
                  <select
                    value={catCategory}
                    onChange={(e) => { setCatCategory(e.target.value); setCatSubcategory(''); }}
                    className="mt-1 w-full border border-[#D4C4B0] bg-white px-3 py-2 text-sm rounded-none"
                    data-testid="cs-cat-category"
                  >
                    <option value="">Alle Kategorien</option>
                    {catCategories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[#5D4E3C] text-xs">Unterkategorie</Label>
                  <select
                    value={catSubcategory}
                    onChange={(e) => setCatSubcategory(e.target.value)}
                    disabled={!catCategory || !(catSubsByCat[catCategory] || []).length}
                    className="mt-1 w-full border border-[#D4C4B0] bg-white px-3 py-2 text-sm rounded-none disabled:bg-[#F5F0E8] disabled:text-[#A89A85]"
                    data-testid="cs-cat-subcategory"
                  >
                    <option value="">Alle Unterkategorien</option>
                    {(catSubsByCat[catCategory] || []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cs-cat-min" className="text-[#5D4E3C] text-xs">Mindestpreis (€)</Label>
                  <Input
                    id="cs-cat-min"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="z.B. 10"
                    value={catMinPrice}
                    onChange={(e) => setCatMinPrice(e.target.value)}
                    className="rounded-none border-[#D4C4B0] mt-1"
                    data-testid="cs-cat-min-price"
                  />
                </div>
                <div>
                  <Label htmlFor="cs-cat-max" className="text-[#5D4E3C] text-xs">Höchstpreis (€)</Label>
                  <Input
                    id="cs-cat-max"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="z.B. 100"
                    value={catMaxPrice}
                    onChange={(e) => setCatMaxPrice(e.target.value)}
                    className="rounded-none border-[#D4C4B0] mt-1"
                    data-testid="cs-cat-max-price"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-[#5D4E3C] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={catHandmadeOnly}
                    onChange={(e) => setCatHandmadeOnly(e.target.checked)}
                    data-testid="cs-cat-handmade-only"
                  />
                  Nur handgefertigte Produkte
                </label>
                <label className="flex items-center gap-2 text-sm text-[#5D4E3C] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={catIncludeImages}
                    onChange={(e) => setCatIncludeImages(e.target.checked)}
                    data-testid="cs-cat-include-images"
                  />
                  Produktbilder einbetten
                  <span className="text-xs text-[#8B7355]">(deaktivieren für schnellere/schlankere PDFs)</span>
                </label>
              </div>

              <div>
                <Label htmlFor="cs-cat-title" className="text-[#5D4E3C] text-xs">Titel (optional)</Label>
                <Input
                  id="cs-cat-title"
                  placeholder="z.B. Frühjahrskollektion 2026"
                  value={catTitle}
                  onChange={(e) => setCatTitle(e.target.value)}
                  className="rounded-none border-[#D4C4B0] mt-1"
                  data-testid="cs-cat-title"
                />
              </div>

              <div>
                <Label htmlFor="cs-cat-intro" className="text-[#5D4E3C] text-xs">Einleitung (optional)</Label>
                <Textarea
                  id="cs-cat-intro"
                  rows={3}
                  placeholder="Ein paar warme Worte über deine Kollektion…"
                  value={catIntro}
                  onChange={(e) => setCatIntro(e.target.value)}
                  className="rounded-none border-[#D4C4B0] mt-1"
                  data-testid="cs-cat-intro"
                />
              </div>

              <div className="bg-[#FAF5EE] border border-[#E8DCC8] p-3 text-xs text-[#8B7355]">
                <strong className="text-[#5D4E3C]">Hinweis:</strong> Die PDF-Erstellung kann bei vielen Produkten mit Bildern bis zu ~30 Sekunden dauern.
                Maximal werden {200} Produkte berücksichtigt. Bei Timeouts: Bilder abschalten oder eine engere Kategorie wählen.
              </div>

              <Button
                onClick={handleDownloadCatalog}
                disabled={catalogLoading}
                className="w-full rounded-none bg-[#5D4E3C] hover:bg-[#4A3E2F] text-white"
                data-testid="cs-cat-download-btn"
              >
                {catalogLoading
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Katalog wird erstellt…</>
                  : <><Download className="h-4 w-4 mr-2" /> Katalog-PDF herunterladen</>}
              </Button>
              <Button
                onClick={openEmailForCatalog}
                disabled={catalogLoading}
                variant="outline"
                className="w-full rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#E8DCC8]"
                data-testid="cs-cat-email-btn"
              >
                <Mail className="h-4 w-4 mr-2" /> Per E-Mail senden
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ display: mode === 'catalog' ? 'none' : undefined }}>
          {/* LEFT: Generator form */}
          <Card className="rounded-none border-[#D4C4B0]">
            <CardHeader className="border-b border-[#E8DCC8]">
              <CardTitle className="text-[#5D4E3C] flex items-center gap-2">
                <Wand2 className="h-5 w-5" />
                {mode === 'social' && 'Neuer Social-Post'}
                {mode === 'blog' && 'Neuer Blog-Beitrag'}
                {mode === 'homepage' && 'Neue Homepage-Sektion'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              {/* Platform (nur für Social) */}
              {mode === 'social' && (
                <div>
                  <Label className="text-[#5D4E3C]">Plattform</Label>
                  <div className="flex gap-2 mt-1.5">
                    {PLATFORMS.map((p) => {
                      const Icon = p.icon;
                      const active = platform === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setPlatform(p.id)}
                          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 border text-sm transition-colors ${
                            active
                              ? 'bg-[#5D4E3C] text-white border-[#5D4E3C]'
                              : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:bg-[#F5F0E8]'
                          }`}
                          data-testid={`cs-platform-${p.id}`}
                        >
                          <Icon className="h-4 w-4" style={!active ? { color: p.color } : {}} />
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Section-Type (nur für Homepage) */}
              {mode === 'homepage' && (
                <div>
                  <Label className="text-[#5D4E3C]">Sektion</Label>
                  <select
                    value={hpSectionType}
                    onChange={(e) => setHpSectionType(e.target.value)}
                    className="mt-1.5 w-full border border-[#D4C4B0] bg-white px-3 py-2 text-sm rounded-none"
                    data-testid="cs-hp-section-type"
                  >
                    <option value="hero">Hero (Top-Banner)</option>
                    <option value="about">Über uns / Über mich</option>
                    <option value="benefits">Vorteile / USP</option>
                    <option value="testimonial_intro">Kundenstimmen-Intro</option>
                    <option value="category_intro">Kategorie-Intro</option>
                    <option value="cta_banner">CTA-Banner</option>
                  </select>
                </div>
              )}

              {/* Tone (alle Modi) */}
              <div>
                <Label className="text-[#5D4E3C]">Tonalität</Label>
                <div className="flex gap-2 mt-1.5 overflow-x-auto pb-1">
                  {TONES.map((t) => {
                    const Icon = t.icon;
                    const active = tone === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTone(t.id)}
                        className={`flex items-center gap-1.5 py-1.5 px-3 border text-xs whitespace-nowrap transition-colors ${
                          active
                            ? 'bg-[#C4A084] text-white border-[#C4A084]'
                            : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:bg-[#F5F0E8]'
                        }`}
                        data-testid={`cs-tone-${t.id}`}
                      >
                        <Icon className="h-3.5 w-3.5" /> {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Blog length (nur Blog) */}
              {mode === 'blog' && (
                <div>
                  <Label className="text-[#5D4E3C]">Länge</Label>
                  <div className="flex gap-2 mt-1.5">
                    {[
                      { id: 'short', label: 'Kurz (~300 W)' },
                      { id: 'medium', label: 'Mittel (~600 W)' },
                      { id: 'long', label: 'Lang (~1200 W)' },
                    ].map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => setBlogLength(l.id)}
                        className={`flex-1 py-1.5 px-2 border text-xs transition-colors ${
                          blogLength === l.id
                            ? 'bg-[#5D4E3C] text-white border-[#5D4E3C]'
                            : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:bg-[#F5F0E8]'
                        }`}
                        data-testid={`cs-blog-length-${l.id}`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Prompt / Topic */}
              <div>
                <Label htmlFor="cs-prompt" className="text-[#5D4E3C]">
                  {mode === 'social' && 'Worum geht es?'}
                  {mode === 'blog' && 'Thema des Blogs'}
                  {mode === 'homepage' && 'Inhalt der Sektion'}
                </Label>
                <Textarea
                  id="cs-prompt"
                  rows={mode === 'blog' ? 3 : 4}
                  placeholder={
                    mode === 'social'
                      ? 'z.B. Neue Boho-Kissen-Kollektion vorstellen'
                      : mode === 'blog'
                      ? 'z.B. 5 Tipps für ein gemütliches Schlafzimmer im Boho-Stil'
                      : 'z.B. Willkommen — handgemachte Deko mit Seele'
                  }
                  value={mode === 'social' ? prompt : mode === 'blog' ? blogTopic : hpTopic}
                  onChange={(e) => {
                    if (mode === 'social') setPrompt(e.target.value);
                    else if (mode === 'blog') setBlogTopic(e.target.value);
                    else setHpTopic(e.target.value);
                  }}
                  className="rounded-none border-[#D4C4B0] mt-1"
                  data-testid="cs-prompt-input"
                />
              </div>

              {/* Audience + CTA-Link (nur Social) / Keywords (nur Blog) */}
              {mode === 'social' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="cs-audience" className="text-[#5D4E3C]">Zielgruppe (optional)</Label>
                    <Input
                      id="cs-audience"
                      placeholder="z.B. junge Mütter, 25-40"
                      value={audience}
                      onChange={(e) => setAudience(e.target.value)}
                      className="rounded-none border-[#D4C4B0] mt-1"
                      data-testid="cs-audience-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cs-cta-link" className="text-[#5D4E3C]">CTA-Link (optional)</Label>
                    <Input
                      id="cs-cta-link"
                      placeholder="https://homeyoursoul.com/…"
                      value={ctaLink}
                      onChange={(e) => setCtaLink(e.target.value)}
                      className="rounded-none border-[#D4C4B0] mt-1"
                      data-testid="cs-cta-link-input"
                    />
                  </div>
                </div>
              )}

              {mode === 'blog' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="cs-blog-audience" className="text-[#5D4E3C]">Zielgruppe (optional)</Label>
                    <Input
                      id="cs-blog-audience"
                      placeholder="z.B. Boho-Liebhaberinnen, Wohnungs-Einrichter"
                      value={blogAudience}
                      onChange={(e) => setBlogAudience(e.target.value)}
                      className="rounded-none border-[#D4C4B0] mt-1"
                      data-testid="cs-blog-audience-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cs-keywords" className="text-[#5D4E3C]">SEO-Keywords (kommagetrennt, optional)</Label>
                    <Input
                      id="cs-keywords"
                      placeholder="Boho, Schlafzimmer, Deko"
                      value={blogKeywords}
                      onChange={(e) => setBlogKeywords(e.target.value)}
                      className="rounded-none border-[#D4C4B0] mt-1"
                      data-testid="cs-keywords-input"
                    />
                  </div>
                </div>
              )}

              {/* Products include (Social + Blog) */}
              {mode !== 'homepage' && (
                <label className="flex items-center gap-2 text-sm text-[#5D4E3C] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeProducts}
                    onChange={(e) => setIncludeProducts(e.target.checked)}
                    data-testid="cs-include-products-checkbox"
                  />
                  Aktuelle Shop-Produkte als Inspiration einbeziehen
                </label>
              )}

              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full rounded-none bg-[#5D4E3C] hover:bg-[#4A3E2F] text-white"
                data-testid="cs-generate-btn"
              >
                {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {generating ? 'KI denkt nach…' : 'Generieren'}
              </Button>
            </CardContent>
          </Card>

          {/* RIGHT: Result + refinement */}
          <Card className="rounded-none border-[#D4C4B0]">
            <CardHeader className="border-b border-[#E8DCC8] flex flex-row items-center justify-between">
              <CardTitle className="text-[#5D4E3C] flex items-center gap-2">
                {mode === 'social' && platformMeta && <platformMeta.icon className="h-5 w-5" style={{ color: platformMeta.color }} />}
                {mode === 'blog' && <BookOpen className="h-5 w-5 text-[#5D4E3C]" />}
                {mode === 'homepage' && <Layout className="h-5 w-5 text-[#5D4E3C]" />}
                Vorschau & Anpassung
              </CardTitle>
              {draft && (
                <span className="text-[10px] px-2 py-0.5 bg-[#E8DCC8] text-[#5D4E3C]">{draft.status}</span>
              )}
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              {!draft ? (
                <div className="text-center py-10 text-[#8B7355]">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Noch kein Entwurf — links generieren oder aus dem Verlauf laden</p>
                </div>
              ) : (
                <>
                  {/* ----- SOCIAL POST FIELDS ----- */}
                  {(draft.content_type === undefined || draft.content_type === 'social' || draft.caption !== undefined) && draft.content_type !== 'blog' && draft.content_type !== 'homepage_section' && (
                    <>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs flex items-center justify-between">
                          Titel (intern)
                          <button onClick={() => handleCopy(draft.title || '')} className="text-[#8B7355] hover:text-[#5D4E3C]"><Copy className="h-3.5 w-3.5" /></button>
                        </Label>
                        <Input value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" data-testid="cs-draft-title" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs flex items-center justify-between">
                          Caption
                          <button onClick={() => handleCopy(draft.caption || '')} className="text-[#8B7355] hover:text-[#5D4E3C]"><Copy className="h-3.5 w-3.5" /></button>
                        </Label>
                        <Textarea rows={6} value={draft.caption || ''} onChange={(e) => setDraft({ ...draft, caption: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" data-testid="cs-draft-caption" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs flex items-center justify-between">
                          Hashtags
                          <button onClick={() => handleCopy((draft.hashtags || []).join(' '))} className="text-[#8B7355] hover:text-[#5D4E3C]"><Copy className="h-3.5 w-3.5" /></button>
                        </Label>
                        <Textarea rows={2} value={(draft.hashtags || []).join(' ')} onChange={(e) => setDraft({ ...draft, hashtags: e.target.value.split(/\s+/).filter(Boolean) })} className="rounded-none border-[#D4C4B0] mt-1 text-xs" data-testid="cs-draft-hashtags" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">CTA</Label>
                        <Input value={draft.cta || ''} onChange={(e) => setDraft({ ...draft, cta: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" data-testid="cs-draft-cta" />
                      </div>
                    </>
                  )}

                  {/* ----- BLOG FIELDS ----- */}
                  {draft.content_type === 'blog' && (
                    <>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs flex items-center justify-between">
                          Blog-Titel
                          <button onClick={() => handleCopy(draft.title || '')} className="text-[#8B7355] hover:text-[#5D4E3C]"><Copy className="h-3.5 w-3.5" /></button>
                        </Label>
                        <Input value={draft.title || ''} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" data-testid="cs-blog-title" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[#5D4E3C] text-xs">URL-Slug</Label>
                          <Input value={draft.slug || ''} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1 text-xs" />
                        </div>
                        <div>
                          <Label className="text-[#5D4E3C] text-xs">Tags</Label>
                          <Input value={(draft.tags || []).join(', ')} onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(',').map(s=>s.trim()).filter(Boolean) })} className="rounded-none border-[#D4C4B0] mt-1 text-xs" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Meta-Description (SEO)</Label>
                        <Textarea rows={2} value={draft.meta_description || ''} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Einleitung</Label>
                        <Textarea rows={3} value={draft.intro || ''} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Abschnitte</Label>
                        <div className="space-y-2 mt-1">
                          {(draft.sections || []).map((sec, idx) => (
                            <div key={idx} className="border border-[#E8DCC8] p-2 space-y-1">
                              <Input
                                value={sec.heading || ''}
                                placeholder="Zwischenüberschrift"
                                onChange={(e) => {
                                  const newSecs = [...(draft.sections||[])];
                                  newSecs[idx] = { ...newSecs[idx], heading: e.target.value };
                                  setDraft({ ...draft, sections: newSecs });
                                }}
                                className="rounded-none border-[#D4C4B0] text-sm font-semibold"
                              />
                              <Textarea
                                rows={4}
                                value={sec.content || ''}
                                onChange={(e) => {
                                  const newSecs = [...(draft.sections||[])];
                                  newSecs[idx] = { ...newSecs[idx], content: e.target.value };
                                  setDraft({ ...draft, sections: newSecs });
                                }}
                                className="rounded-none border-[#D4C4B0] text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Schluss</Label>
                        <Textarea rows={3} value={draft.conclusion || ''} onChange={(e) => setDraft({ ...draft, conclusion: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" />
                      </div>
                    </>
                  )}

                  {/* ----- HOMEPAGE SECTION FIELDS ----- */}
                  {draft.content_type === 'homepage_section' && (
                    <>
                      <p className="text-xs text-[#8B7355]">Sektionstyp: <strong>{draft.section_type}</strong></p>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Headline</Label>
                        <Input value={draft.headline || ''} onChange={(e) => setDraft({ ...draft, headline: e.target.value, title: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Subline</Label>
                        <Input value={draft.subline || ''} onChange={(e) => setDraft({ ...draft, subline: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Body</Label>
                        <Textarea rows={5} value={draft.body || ''} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" />
                      </div>
                      <div>
                        <Label className="text-[#5D4E3C] text-xs">Button-Text (CTA)</Label>
                        <Input value={draft.cta_label || ''} onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })} className="rounded-none border-[#D4C4B0] mt-1" />
                      </div>
                    </>
                  )}

                  {/* Image prompt - common */}
                  <div>
                    <Label className="text-[#5D4E3C] text-xs flex items-center gap-1">
                      <ImageIcon className="h-3.5 w-3.5" /> Bild-Vorschlag
                    </Label>
                    <Textarea
                      rows={2}
                      value={draft.image_prompt || ''}
                      onChange={(e) => setDraft({ ...draft, image_prompt: e.target.value })}
                      className="rounded-none border-[#D4C4B0] mt-1 text-xs"
                      data-testid="cs-draft-image-prompt"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={handleSaveEdits}
                      className="flex-1 rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#E8DCC8]"
                      data-testid="cs-save-edits-btn"
                    >
                      <Save className="h-4 w-4 mr-2" /> Speichern
                    </Button>
                    <Button
                      onClick={handleDownloadPdf}
                      disabled={downloading}
                      className="flex-1 rounded-none bg-[#8B7355] text-white hover:bg-[#6D5A47]"
                      data-testid="cs-download-pdf-btn"
                    >
                      {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                      PDF
                    </Button>
                    <Button
                      onClick={openEmailForDraft}
                      variant="outline"
                      className="flex-1 rounded-none border-[#C4A084] text-[#5D4E3C] hover:bg-[#E8DCC8]"
                      data-testid="cs-draft-email-btn"
                    >
                      <Mail className="h-4 w-4 mr-2" /> Per E-Mail
                    </Button>
                  </div>

                  {/* Approval (Social only) */}
                  {(draft.content_type === undefined || draft.content_type === 'social' || draft.caption !== undefined) && draft.content_type !== 'blog' && draft.content_type !== 'homepage_section' && (
                    <Button
                      onClick={handleRequestApproval}
                      className="w-full rounded-none bg-[#C4A084] text-white hover:bg-[#B89968]"
                      disabled={draft.status === 'awaiting_approval'}
                      data-testid="cs-request-approval-btn"
                    >
                      <Send className="h-4 w-4 mr-2" /> Freigabe anfordern
                    </Button>
                  )}

                  {/* Refinement */}
                  <div className="border-t border-[#E8DCC8] pt-4">
                    <Label className="text-[#5D4E3C] flex items-center gap-1.5">
                      <RotateCcw className="h-4 w-4" /> KI-Anpassung
                    </Label>
                    <Textarea
                      rows={2}
                      placeholder='z.B. "Etwas kürzer und mit mehr Emojis"'
                      value={refineInstruction}
                      onChange={(e) => setRefineInstruction(e.target.value)}
                      className="rounded-none border-[#D4C4B0] mt-1.5"
                      data-testid="cs-refine-instruction"
                    />
                    <Button
                      onClick={handleRefine}
                      disabled={refining || !refineInstruction.trim()}
                      className="w-full mt-2 rounded-none bg-[#5D4E3C] hover:bg-[#4A3E2F] text-white"
                      data-testid="cs-refine-btn"
                    >
                      {refining ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                      {refining ? 'KI passt an…' : 'Mit KI anpassen'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <SendByEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        files={emailFiles}
        defaultSubject={emailDefaults.subject}
        defaultMessage={emailDefaults.message}
      />
    </div>
  );
};

export default ContentStudio;
